import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ShoppingBag, Package } from 'lucide-react';
import { getCart, removeFromCart, updateQuantity, clearCart, calculateTotal } from '../data/cart';
import { useToast } from '../hooks/use-toast';

const Cart = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [cart, setCart] = useState({ items: [], total: 0 });

  useEffect(() => {
    refreshCart();
  }, []);

  const refreshCart = () => {
    setCart(getCart());
  };

  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
    refreshCart();
    toast({
      title: "Item Removed",
      description: "Product removed from cart successfully.",
    });
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(itemId, newQuantity);
    refreshCart();
  };

  const handleClearCart = () => {
    clearCart();
    refreshCart();
    toast({
      title: "Cart Cleared",
      description: "All items removed from cart.",
    });
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) {
      toast({
        title: "Cart is Empty",
        description: "Please add items to cart before checkout.",
        variant: "destructive"
      });
      return;
    }
    navigate('/checkout');
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
        <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl font-bold mb-6">Shopping Cart</h1>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ShoppingCart className="h-32 w-32 text-gray-300 mx-auto mb-8" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your Cart is Empty</h2>
            <p className="text-xl text-gray-600 mb-8">
              Looks like you haven't added any products yet
            </p>
            <Button 
              onClick={() => navigate('/shop')}
              size="lg"
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              <ShoppingBag className="h-5 w-5 mr-2" />
              Start Shopping
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-5xl font-bold mb-4">Shopping Cart</h1>
              <p className="text-xl text-cyan-50">
                {cart.items.length} {cart.items.length === 1 ? 'item' : 'items'} in your cart
              </p>
            </div>
            <Button 
              onClick={handleClearCart}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10"
            >
              Clear Cart
            </Button>
          </div>
        </div>
      </section>

      {/* Cart Items */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items List */}
            <div className="lg:col-span-2 space-y-6">
              {cart.items.map((item) => (
                <Card key={item.id} className="border-2 hover:border-cyan-300 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex gap-6">
                      {/* Product Image */}
                      <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden border-2 border-gray-200">
                        <img 
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Details */}
                      <div className="flex-grow">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <Badge className="mb-2 bg-cyan-100 text-cyan-700 border-0">
                              {item.category}
                            </Badge>
                            <h3 className="text-xl font-bold text-gray-900">{item.name}</h3>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Add-ons */}
                        {item.addons && item.addons.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-600 mb-1">Add-ons:</p>
                            <div className="space-y-1">
                              {item.addons.map((addon, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-700">+ {addon.name}</span>
                                  <span className="font-semibold text-gray-900">₹{addon.price.toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Quantity and Price */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              disabled={item.quantity <= 1}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-lg font-semibold w-8 text-center">
                              {item.quantity}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>

                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">
                              ₹{((item.price * item.quantity) + item.addons.reduce((sum, a) => sum + a.price, 0)).toLocaleString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              ₹{item.price.toLocaleString()} each
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="border-2 border-cyan-200 shadow-xl sticky top-24">
                <CardHeader>
                  <CardTitle className="text-2xl">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-gray-700">
                    <span>Subtotal:</span>
                    <span className="font-semibold">₹{cart.total.toLocaleString()}</span>
                  </div>

                  <div className="flex items-center justify-between text-gray-700">
                    <span>GST (18%):</span>
                    <span className="font-semibold">₹{Math.round(cart.total * 0.18).toLocaleString()}</span>
                  </div>

                  <div className="flex items-center justify-between text-green-600">
                    <span>Shipping:</span>
                    <span className="font-semibold">FREE</span>
                  </div>

                  <div className="border-t pt-4 flex items-center justify-between">
                    <span className="text-xl font-bold text-gray-900">Total:</span>
                    <span className="text-3xl font-bold text-gray-900">
                      ₹{Math.round(cart.total * 1.18).toLocaleString()}
                    </span>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white text-lg py-6"
                  >
                    Proceed to Checkout
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>

                  <Button
                    onClick={() => navigate('/shop')}
                    variant="outline"
                    className="w-full border-2 border-cyan-500 text-cyan-600"
                  >
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    Continue Shopping
                  </Button>

                  {/* Trust Badges */}
                  <div className="pt-4 border-t space-y-3">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Package className="h-4 w-4 text-green-600" />
                      <span>Free shipping on all orders</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <ShoppingCart className="h-4 w-4 text-green-600" />
                      <span>7-day easy returns</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Badge className="bg-green-100 text-green-700 border-0">
                        Secure Checkout
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
